import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button as AntButton,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Upload,
} from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { Dayjs } from "dayjs";
import { ClipboardList, Eye, Loader2, RotateCcw } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Select } from "../../common";
import { SeparationPageSkeleton } from "../Components/SeparationPageSkeleton";
import {
  useCancelTerminationMutation,
  useGetTerminationFormContextQuery,
  useGetTerminationSeparationGateForEmployeeQuery,
  useInitiateTerminationMutation,
  useLazyGetTerminationQuery,
  useListTerminationsQuery,
  useTerminationActionMutation,
  useUploadTerminationSupportingDocumentMutation,
} from "../../../store/apis/termination.api";
import {
  useGetAllOrgUsersForFilterQuery,
  useGetUsersForFilterQuery,
} from "../../../store/apis/user.api";
import { useGetMasterConfigByCategoryQuery } from "../../../store/apis/masterConfig.api";
import { MasterConfigCategory } from "../../../constants";
import {
  buildEmploymentTypeIdToLabelMap,
  employmentTypeDisplayForRow,
} from "../Utils/employmentTypeDisplay";
import type {
  InitiateTerminationBody,
  TerminationDecision,
  TerminationRequestDto,
  TerminationSupportingDocumentRef,
  TerminationTypeOption,
} from "../Utils/types";
import { pickTerminationUploadFields } from "../Utils/attachmentUtils";
import {
  separationDocumentUploadItemRender,
  separationDocumentUploadListClassName,
  separationDocumentUploadSideBySideClassName,
} from "../Components/separationDocumentUploadItemRender";
import {
  formatExitDateDisplay,
  SEPARATION_DATE_DISPLAY_FORMAT,
  utcYmdFromPickerCurrent,
} from "../Utils/exitDateUtils";
import { getErrorMessage } from "../../../store/utils/apiError";
import { formLabelWithInlineHint } from "../Components/formLabelWithInlineHint";
import { separationFormRequiredMark } from "../Components/separationFormRequiredMark";
import { separationTextAreaClassName } from "../Utils/separationTextAreaClassName";
import { useAuth } from "../../../store/hooks/useAuth";
import { useHasPermission } from "../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import { ConfigurableTable } from "../../common";
import { SeparationAlert } from "../Components/SeparationAlert";
import Button from "../../common/Button";
import SimpleTooltip from "../../common/SimpleTooltip";
import type { TableColumn } from "../../common/Table";
import TerminationViewModal from "./components/TerminationViewModal";
import { SeparationStatusBadge } from "../Components/SeparationStatusBadge";
import {
  getTerminationRowStatusVariant,
  terminationCanReview,
  terminationEmployeeLabel,
  terminationListStatusLabel,
} from "../Utils/terminationStatusUtils";

dayjs.extend(utc);

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_EXT = /\.(pdf|doc|docx)$/i;

type InitiateFormValues = {
  employeeId: string;
  terminationTypeId: string;
  reasonJustification: string;
  lastWorkingDay?: Dayjs;
  confidentialityNote?: string;
};

type ActionFormValues = {
  decision: TerminationDecision;
  remarks?: string;
};

type CancelFormValues = {
  reason?: string;
};

function typeLabel(opt: TerminationTypeOption): string {
  const t = opt.displayName?.trim();
  return t || opt.id;
}

function isAfterApprovalNotifySetting(value: string | undefined): boolean {
  if (!value) return false;
  return String(value).toUpperCase().includes("AFTER");
}

const TerminationListPage: React.FC = () => {
  const { user } = useAuth();
  const sessionReady = Boolean(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const canView = useHasPermission(PERMISSIONS.TERMINATION_VIEW);
  const hasTerminationUmbrella = useHasPermission(PERMISSIONS.TERMINATION);
  const hasLegacyTerminationManage = useHasPermission(
    PERMISSIONS.TERMINATION_MANAGE,
  );
  const canInitiate = useHasPermission(PERMISSIONS.TERMINATION_INITIATE_MANAGE);
  const canApprovals = useHasPermission(
    PERMISSIONS.TERMINATION_APPROVALS_MANAGE,
  );
  /** List + detail: view, initiate, approve, or legacy/umbrella codes from backend */
  const canOpenDetail =
    canView ||
    canInitiate ||
    canApprovals ||
    hasTerminationUmbrella ||
    hasLegacyTerminationManage;
  /** Form-context + initiate UI (not TERMINATION_VIEW alone) */
  const canShowInitiateForm =
    canInitiate ||
    canApprovals ||
    hasTerminationUmbrella ||
    hasLegacyTerminationManage;
  /** Org-wide employee picker + confidentiality: explicit HR approvals only */
  const canHrScope = canApprovals;
  /** Approve/reject + refetch detail for HR fields */
  const canApproveWorkflow =
    canApprovals || hasTerminationUmbrella || hasLegacyTerminationManage;
  const canCancelTermination =
    canInitiate ||
    canApprovals ||
    hasTerminationUmbrella ||
    hasLegacyTerminationManage;

  const {
    data: ctx,
    isLoading: ctxLoading,
    isError: ctxError,
    refetch: refetchFormContext,
  } = useGetTerminationFormContextQuery(undefined, {
    skip: !sessionReady || !canShowInitiateForm,
  });
  const { data: reportees = [] } = useGetUsersForFilterQuery(
    { reporteesOnly: true },
    { skip: !sessionReady || !canShowInitiateForm || canHrScope },
  );
  const { data: orgUsers = [] } = useGetAllOrgUsersForFilterQuery(undefined, {
    skip: !sessionReady || !canShowInitiateForm || !canHrScope,
  });
  const {
    data: rows = [],
    isLoading,
    isFetching,
    refetch,
  } = useListTerminationsQuery(undefined, {
    skip: !sessionReady || !canOpenDetail,
  });
  const { data: employmentTypeMasterList = [] } =
    useGetMasterConfigByCategoryQuery(MasterConfigCategory.EMPLOYMENT_TYPE, {
      skip: !sessionReady || !canOpenDetail,
    });
  const employmentTypeIdToLabel = useMemo(
    () => buildEmploymentTypeIdToLabelMap(employmentTypeMasterList),
    [employmentTypeMasterList],
  );
  const [lazyGet, lazyResult] = useLazyGetTerminationQuery();
  const [terminationAction, { isLoading: acting }] =
    useTerminationActionMutation();
  const [cancelTermination, { isLoading: cancelling }] =
    useCancelTerminationMutation();
  const [initiate, { isLoading: submitting }] =
    useInitiateTerminationMutation();
  const [uploadDoc] =
    useUploadTerminationSupportingDocumentMutation();
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [isUploadingPipDoc, setIsUploadingPipDoc] = useState(false);

  const [initiateForm] = Form.useForm<InitiateFormValues>();
  const selectedEmployeeId = Form.useWatch("employeeId", initiateForm) as
    | string
    | undefined;
  const separationGateSubjectId = useMemo(() => {
    const t = selectedEmployeeId != null ? String(selectedEmployeeId).trim() : "";
    return t;
  }, [selectedEmployeeId]);
  const {
    data: separationGate,
    isFetching: gateLoading,
    isError: gateError,
  } = useGetTerminationSeparationGateForEmployeeQuery(separationGateSubjectId, {
    skip: !sessionReady || !canShowInitiateForm || !separationGateSubjectId,
  });
  /** When an employee is chosen, keep fields disabled until the gate says initiation is allowed. */
  const hasSubjectEmployee = Boolean(separationGateSubjectId);
  const initiateFieldsLocked =
    hasSubjectEmployee &&
    (gateLoading || gateError || separationGate?.canInitiateTermination === false);

  useEffect(() => {
    if (hasSubjectEmployee && gateError) {
      toast.error("Could not verify termination eligibility");
    }
  }, [hasSubjectEmployee, gateError]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [supportingDocuments, setSupportingDocuments] = useState<
    TerminationSupportingDocumentRef[]
  >([]);
  const [pipFileList, setPipFileList] = useState<UploadFile[]>([]);
  const [pipDocuments, setPipDocuments] = useState<
    TerminationSupportingDocumentRef[]
  >([]);

  const [viewRow, setViewRow] = useState<TerminationRequestDto | null>(null);
  const [actionRow, setActionRow] = useState<TerminationRequestDto | null>(
    null,
  );
  const [cancelRow, setCancelRow] = useState<TerminationRequestDto | null>(
    null,
  );
  const [actionForm] = Form.useForm<ActionFormValues>();
  const [cancelForm] = Form.useForm<CancelFormValues>();

  const employeeOptions = useMemo(
    () => (canHrScope ? orgUsers : reportees),
    [canHrScope, orgUsers, reportees],
  );
  const typeOptions = useMemo(() => ctx?.terminationTypeOptions ?? [], [ctx]);

  /** Deep link from employee My exit (`?openTermination=id`). */
  useEffect(() => {
    const openId = searchParams.get("openTermination")?.trim();
    if (!openId || !sessionReady || !canOpenDetail) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await lazyGet(openId).unwrap();
        if (!cancelled) setViewRow(detail);
      } catch {
        toast.error("Could not open that termination request.");
      } finally {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete("openTermination");
              return next;
            },
            { replace: true },
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, sessionReady, canOpenDetail, lazyGet, setSearchParams]);

  /** Deep link from employee My exit (`?openTermination=id`). */
  useEffect(() => {
    const openId = searchParams.get("openTermination")?.trim();
    if (!openId || !sessionReady || !canOpenDetail) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await lazyGet(openId).unwrap();
        if (!cancelled) setViewRow(detail);
      } catch {
        toast.error("Could not open that termination request.");
      } finally {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete("openTermination");
              return next;
            },
            { replace: true },
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, sessionReady, canOpenDetail, lazyGet, setSearchParams]);

  const sortedRows = useMemo(() => {
    const ts = (r: TerminationRequestDto) =>
      Date.parse(r.createdAt ?? "") || Date.parse(r.updatedAt ?? "") || 0;
    return [...rows].sort((a, b) => {
      const d = ts(b) - ts(a);
      if (d !== 0) return d;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [rows]);

  const onInitiateFinish = async (values: InitiateFormValues) => {
    if (!ctx) return;
    if (values.employeeId && separationGate && !separationGate.canInitiateTermination) {
      toast.error(
        (separationGate.blockMessage && String(separationGate.blockMessage).trim()) ||
        "Termination cannot be started for this employee while a resignation or an existing termination is in progress.",
      );
      return;
    }
    if (!values.lastWorkingDay || !dayjs.isDayjs(values.lastWorkingDay)) {
      toast.error("Select last working date.");
      return;
    }
    const ymd = utcYmdFromPickerCurrent(values.lastWorkingDay);
    const min = dayjs.utc(ctx.serverDate, "YYYY-MM-DD").startOf("day");
    const picked = dayjs.utc(ymd, "YYYY-MM-DD").startOf("day");
    if (picked.valueOf() < min.valueOf()) {
      toast.error("Last working date cannot be before today (server date).");
      return;
    }
    const body: InitiateTerminationBody = {
      employeeId: values.employeeId,
      terminationTypeId: values.terminationTypeId,
      reasonJustification: values.reasonJustification.trim(),
      lastWorkingDay: `${ymd}T00:00:00.000Z`,
      supportingDocuments:
        supportingDocuments.length > 0 ? supportingDocuments : undefined,
      warningPipDocuments:
        pipDocuments.length > 0 ? pipDocuments : undefined,
      confidentialityNote:
        canHrScope && values.confidentialityNote?.trim()
          ? values.confidentialityNote.trim()
          : undefined,
    };

    try {
      const created = await initiate(body).unwrap();
      const st = String(created.terminationStatus ?? "").toUpperCase();
      const workflowDone = Boolean(created.workflow?.isCompleted);
      const approvedNoticePath = st === "NOTICE_PERIOD" && workflowDone;
      toast.success(
        approvedNoticePath
          ? "Termination approved (auto-approved). Notice / exit window is active — shared exit steps use your exit record."
          : "Termination initiated",
      );
      initiateForm.resetFields();
      setFileList([]);
      setSupportingDocuments([]);
      setPipFileList([]);
      setPipDocuments([]);
      if (canShowInitiateForm) void refetchFormContext();
      if (canOpenDetail) void refetch();
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "The termination initiation could not be completed");
    }
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (file.size > MAX_BYTES) {
      toast.error("File must be 10MB or smaller.");
      return Upload.LIST_IGNORE;
    }
    if (!ACCEPT_EXT.test(file.name)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      return Upload.LIST_IGNORE;
    }
    setIsUploadingDoc(true);
    void (async () => {
      try {
        const res = await uploadDoc(file).unwrap();
        const fields = pickTerminationUploadFields(res, file.name);
        if (!fields.fileUrl && !fields.fileKey) {
          toast.error("Upload succeeded but no file reference was returned.");
          return;
        }
        setSupportingDocuments((prev) => [...prev, fields]);
        setFileList((prev) => [
          ...prev,
          { uid: file.uid, name: file.name, status: "done" },
        ]);
        toast.success("Supporting document uploaded successfully");
      } catch (e) {
        toast.error(getErrorMessage(e as never) ?? "The document upload could not be completed");
      } finally {
        setIsUploadingDoc(false);
      }
    })();
    return false;
  };

  const handlePipBeforeUpload = (file: RcFile) => {
    if (file.size > MAX_BYTES) {
      toast.error("File must be 10MB or smaller.");
      return Upload.LIST_IGNORE;
    }
    if (!ACCEPT_EXT.test(file.name)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      return Upload.LIST_IGNORE;
    }
    setIsUploadingPipDoc(true);
    void (async () => {
      try {
        const res = await uploadDoc(file).unwrap();
        const fields = pickTerminationUploadFields(res, file.name);
        if (!fields.fileUrl && !fields.fileKey) {
          toast.error("Upload succeeded but no file reference was returned.");
          return;
        }
        setPipDocuments((prev) => [...prev, fields]);
        setPipFileList((prev) => [
          ...prev,
          { uid: file.uid, name: file.name, status: "done" },
        ]);
        toast.success("Supporting document uploaded successfully");
      } catch (e) {
        toast.error(getErrorMessage(e as never) ?? "The document upload could not be completed");
      } finally {
        setIsUploadingPipDoc(false);
      }
    })();
    return false;
  };

  const openView = useCallback(
    async (row: TerminationRequestDto) => {
      setViewRow(row);
      if (canApproveWorkflow) {
        try {
          const detail = await lazyGet(row.id).unwrap();
          setViewRow(detail);
        } catch {
          // keep list row if detail fails
        }
      }
    },
    [canApproveWorkflow, lazyGet],
  );

  const closeView = () => {
    setViewRow(null);
  };

  const openAction = (row: TerminationRequestDto) => {
    setActionRow(row);
    actionForm.setFieldsValue({
      decision: "approved",
      remarks: undefined,
    });
  };

  const openCancel = (row: TerminationRequestDto) => {
    setCancelRow(row);
    cancelForm.resetFields();
  };

  const submitAction = async () => {
    if (!actionRow) return;
    try {
      const values = await actionForm.validateFields();
      await terminationAction({
        id: actionRow.id,
        body: {
          decision: values.decision,
          remarks: values.remarks?.trim() || undefined,
        },
      }).unwrap();
      toast.success(
        values.decision === "approved"
          ? "Termination request has been approved successfully"
          : "Termination request has been rejected successfully",
      );
      setActionRow(null);
      actionForm.resetFields();
      if (canOpenDetail) void refetch();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) {
        return Promise.reject(e);
      }
      toast.error(getErrorMessage(e as never) ?? "The requested action could not be completed");
      return Promise.reject(e);
    }
  };

  const submitCancel = async () => {
    if (!cancelRow) return;
    const afterApproval = Boolean(cancelRow.workflow?.isCompleted);
    try {
      const values = await cancelForm.validateFields();
      const reason = values.reason?.trim() ?? "";
      if (afterApproval && !reason) {
        toast.error("Reason is required after final approval.");
        return Promise.reject(new Error("validation"));
      }
      await cancelTermination({
        id: cancelRow.id,
        body: { reason: reason || undefined },
      }).unwrap();
      toast.success("Termination request has been cancelled successfully");
      setCancelRow(null);
      cancelForm.resetFields();
      if (canOpenDetail) void refetch();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) {
        return Promise.reject(e);
      }
      toast.error(getErrorMessage(e as never) ?? "The termination cancellation could not be completed");
      return Promise.reject(e);
    }
  };

  const columns: TableColumn<TerminationRequestDto>[] = useMemo(
    () => [
      {
        key: "employee",
        title: "Employee",
        label: "Employee",
        required: true,
        render: (_, r) => (
          <div className="flex flex-col gap-0.5 items-start min-w-0">
            <span className="break-words font-medium text-slate-900">{terminationEmployeeLabel(r)}</span>
            <span className="text-xs text-slate-500 tabular-nums">
              {r.employee?.employeeCode || r.employeeId || "—"}
            </span>
          </div>
        ),
      },
      {
        key: "employmentType",
        title: "Employment type",
        label: "Employment type",
        defaultVisible: true,
        render: (_, r) =>
          employmentTypeDisplayForRow(r, employmentTypeIdToLabel),
      },
      {
        key: "type",
        title: "Type",
        label: "Type",
        render: (_, r) => r.terminationType?.displayName ?? "—",
      },
      {
        key: "status",
        title: "Approval Status",
        label: "Approval Status",
        render: (_, r) => (
          <SeparationStatusBadge
            label={terminationListStatusLabel(r)}
            variant={getTerminationRowStatusVariant(r)}
          />
        ),
      },
      {
        key: "lwd",
        title: "Last working date",
        label: "Last working date",
        render: (_, r) => formatExitDateDisplay(r.lastWorkingDay),
      },
      {
        key: "created",
        title: "Submitted On",
        label: "Submitted On",
        render: (_, r) =>
          r.createdAt ? formatExitDateDisplay(r.createdAt) : "—",
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        align: "center",
        width: 160,
        truncate: false,
        render: (_, r) => {
          const viewLoading = lazyResult.isFetching && viewRow?.id === r.id;
          const canCancelRow =
            canCancelTermination &&
            !r.workflow?.isCancelled &&
            !String(r.terminationStatus ?? "")
              .toUpperCase()
              .includes("CANCELLED");
          const termIconBtn =
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors";
          return (
            <div className="flex items-center justify-center gap-2 flex-nowrap">
              {canOpenDetail ? (
                <SimpleTooltip
                  label="View detail"
                  side="top"
                  className="inline-flex shrink-0"
                  tooltipClassName="text-xs shadow-md border-0"
                >
                  <button
                    type="button"
                    onClick={() => void openView(r)}
                    aria-label="View detail"
                    disabled={viewLoading}
                    className={
                      viewLoading
                        ? `${termIconBtn} text-blue-500 cursor-wait opacity-70`
                        : `${termIconBtn} text-blue-500 hover:text-blue-600`
                    }
                  >
                    {viewLoading ? (
                      <Loader2
                        className="w-4 h-4 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Eye
                        className="w-4 h-4"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </SimpleTooltip>
              ) : null}
              {canApproveWorkflow && terminationCanReview(r) ? (
                <SimpleTooltip
                  label="Review"
                  side="top"
                  className="inline-flex shrink-0"
                  tooltipClassName="text-xs shadow-md border-0"
                >
                  <button
                    type="button"
                    onClick={() => openAction(r)}
                    aria-label="Review"
                    className={`${termIconBtn} text-orange-500 hover:text-orange-600`}
                  >
                    <ClipboardList
                      className="w-4 h-4"
                      aria-hidden="true"
                    />
                  </button>
                </SimpleTooltip>
              ) : null}
              {canCancelRow ? (
                <SimpleTooltip
                  label="Pull Back"
                  side="top"
                  className="inline-flex shrink-0"
                  tooltipClassName="text-xs shadow-md border-0 max-w-xs"
                >
                  <button
                    type="button"
                    onClick={() => openCancel(r)}
                    aria-label="Pull Back"
                    className={`${termIconBtn} text-gray-500 hover:text-gray-600`}
                  >
                    <RotateCcw
                      className="w-4 h-4"
                      aria-hidden="true"
                    />
                  </button>
                </SimpleTooltip>
              ) : null}
            </div>
          );
        },
      },
    ],
    [
      canApproveWorkflow,
      canCancelTermination,
      canOpenDetail,
      employmentTypeIdToLabel,
      lazyResult.isFetching,
      openAction,
      openCancel,
      openView,
      viewRow?.id,
    ],
  );

  if (!sessionReady) {
    return <SeparationPageSkeleton />;
  }

  const pageInitialLoading =
    (canShowInitiateForm && ctxLoading) || (canOpenDetail && isLoading);

  if (pageInitialLoading) {
    return <SeparationPageSkeleton withForm={true} />;
  }

  return (
    <>
      <div className="bg-surface-muted px-4 sm:px-6 py-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 m-0">
          Termination
        </h1>
      </div>

      <div className="px-4 sm:px-6 pt-0 pb-8 max-w-full mx-auto w-full min-w-0 space-y-6">
        {/* {canOpenDetail && !canShowInitiateForm ? (
          <Alert
            type="info"
            showIcon
            message="Initiate form is not available for your access level"
            description="You can browse the list below."
          />
        ) : null} */}

        {canShowInitiateForm ? (
          <div className="space-y-4">
            {ctxError || !ctx ? (
              <>{/* Form context load error — list still visible below */}</>
            ) : (
              <>
                {canHrScope &&
                  ctx.hrInitiatedTerminationSecondApproverEnabled === false ? (
                  <SeparationAlert
                    type="info"
                    title="HR-initiated terminations skip the second-approver queue"
                    description='For your organization, HR/super-admin requests are auto-approved on submit when policy allows (you will see notice period / exit status immediately after success, not stuck in "initiated").'
                  />
                ) : null}
                {isAfterApprovalNotifySetting(
                  ctx.notifyEmployeeOnTerminationInitiated,
                ) ? (
                  <SeparationAlert
                    type="info"
                    title="Employee notification"
                    description="Employees are notified after approval, not at initiation. HR will notify the employee after the termination is approved."
                  />
                ) : null}

                <Card className="w-full min-w-0 [&_.ant-card-body]:min-w-0">
                  <Form
                    form={initiateForm}
                    layout="vertical"
                    onFinish={(v) => void onInitiateFinish(v)}
                    requiredMark={separationFormRequiredMark}
                  >
                    <Row gutter={[16, 16]} className="mb-4">
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="employeeId"
                          label={formLabelWithInlineHint(
                            "Employee Name",
                            canHrScope ? "" : "",
                          )}
                          rules={[
                            { required: true, message: "Select an employee" },
                          ]}
                          className="mb-0"
                          getValueFromEvent={(v) => v}
                        >
                          <Select
                            placeholder="Select employee"
                            searchable
                            className="w-full"
                            options={employeeOptions.map((u) => ({
                              value: u.id,
                              label: u.fullName?.trim() || u.id,
                            }))}
                            clearable
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="terminationTypeId"
                          label="Termination type"
                          rules={[{ required: true, message: "Select a type" }]}
                          className="mb-0"
                          getValueFromEvent={(v) => v}
                        >
                          <Select
                            placeholder="Select type"
                            searchable
                            className="w-full"
                            disabled={initiateFieldsLocked}
                            options={typeOptions.map((o) => ({
                              value: o.id,
                              label: typeLabel(o),
                            }))}
                            clearable
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {hasSubjectEmployee &&
                      !gateLoading &&
                      !gateError &&
                      separationGate &&
                      !separationGate.canInitiateTermination ? (
                      <SeparationAlert
                        type="warning"
                        className="mb-4"
                        title={
                          (separationGate.blockMessage &&
                            String(separationGate.blockMessage).trim()) ||
                          "Termination cannot be started for this employee while a resignation, exit process, or another termination is already in progress."
                        }
                        description={
                          typeof separationGate.activeTerminationId === "string" &&
                            separationGate.activeTerminationId.trim() &&
                            canOpenDetail ? (
                            <Link
                              to={`/separation/termination?openTermination=${encodeURIComponent(
                                separationGate.activeTerminationId.trim(),
                              )}`}
                              className="text-primary-600 font-medium hover:underline inline-block mt-1"
                            >
                              View the related termination request →
                            </Link>
                          ) : null
                        }
                      />
                    ) : null}
                    {hasSubjectEmployee && gateLoading ? (
                      <SeparationAlert
                        type="info"
                        className="mb-4"
                        title="Checking employee separation status…"
                      />
                    ) : null}

                    <Form.Item
                      name="reasonJustification"
                      label="Reason"
                      rules={[
                        {
                          validator: async (_, v) => {
                            const t = typeof v === "string" ? v.trim() : "";
                            if (!t) {
                              return Promise.reject(new Error("Reason is required"));
                            }
                            return Promise.resolve();
                          },
                        },
                        { max: 1000, message: "Max 1000 characters" },
                      ]}
                    >
                      <Input.TextArea
                        className={separationTextAreaClassName}
                        rows={3}
                        showCount
                        maxLength={1000}
                        placeholder="Comments (max 1000 characters)"
                        disabled={initiateFieldsLocked}
                      />
                    </Form.Item>

                    <Row gutter={[24, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="lastWorkingDay"
                          label="Last working date"
                          rules={[
                            {
                              required: true,
                              message: "Select last working date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-full"
                            format={SEPARATION_DATE_DISPLAY_FORMAT}
                            disabled={initiateFieldsLocked}
                            disabledDate={(current) => {
                              if (!current) return false;
                              const minDate = dayjs
                                .utc(ctx.serverDate, "YYYY-MM-DD")
                                .startOf("day");
                              const selectedUtc = dayjs
                                .utc(current.format("YYYY-MM-DD"))
                                .startOf("day");
                              return selectedUtc.valueOf() < minDate.valueOf();
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label={formLabelWithInlineHint(
                            "Supporting document",
                            "PDF, DOC, or DOCX (Max 10MB)",
                          )}
                        >
                          <Upload
                            className={separationDocumentUploadSideBySideClassName}
                            fileList={fileList}
                            beforeUpload={handleBeforeUpload}
                            itemRender={separationDocumentUploadItemRender}
                            onRemove={(f) => {
                              const idx = fileList.findIndex(
                                (x) => x.uid === f.uid,
                              );
                              setFileList((prev) =>
                                prev.filter((x) => x.uid !== f.uid),
                              );
                              if (idx >= 0) {
                                setSupportingDocuments((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                );
                              }
                            }}
                            multiple
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            disabled={initiateFieldsLocked || isUploadingDoc}
                          >
                            <AntButton
                              disabled={initiateFieldsLocked || isUploadingDoc}
                              icon={
                                <ClipboardList className="w-4 h-4 text-slate-500" />
                              }
                              className="flex items-center gap-2"
                            >
                              {isUploadingDoc ? "Uploading…" : "Upload document"}
                            </AntButton>
                          </Upload>
                        </Form.Item>
                      </Col>
                    </Row>


                    <Form.Item
                      label="PIP Supporting Documents"
                      className="mb-6"
                    >
                      <div className="text-xs text-slate-500 mb-2">
                        Upload warning letters or PIP documentation (Max 10MB)
                      </div>
                      <Upload
                        className={separationDocumentUploadSideBySideClassName}
                        fileList={pipFileList}
                        beforeUpload={handlePipBeforeUpload}
                        itemRender={separationDocumentUploadItemRender}
                        onRemove={(f) => {
                          const idx = pipFileList.findIndex(
                            (x) => x.uid === f.uid,
                          );
                          setPipFileList((prev) =>
                            prev.filter((x) => x.uid !== f.uid),
                          );
                          if (idx >= 0) {
                            setPipDocuments((prev) =>
                              prev.filter((_, i) => i !== idx),
                            );
                          }
                        }}
                        multiple
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        disabled={initiateFieldsLocked || isUploadingPipDoc}
                      >
                        <AntButton
                          disabled={initiateFieldsLocked || isUploadingPipDoc}
                          icon={
                            <ClipboardList className="w-4 h-4 text-slate-500" />
                          }
                          className="flex items-center gap-2"
                        >
                          {isUploadingPipDoc ? "Uploading…" : "Upload references"}
                        </AntButton>
                      </Upload>
                    </Form.Item>

                    {canHrScope ? (
                      <Form.Item
                        name="confidentialityNote"
                        label={formLabelWithInlineHint(
                          "Confidentiality note",
                          "Optional, HR-only visibility",
                        )}
                        rules={[{ max: 2000, message: "Max 2000 characters" }]}
                      >
                        <Input.TextArea
                          className={separationTextAreaClassName}
                          rows={2}
                          showCount
                          maxLength={2000}
                          placeholder="Internal notes for HR/Admins"
                          disabled={initiateFieldsLocked}
                        />
                      </Form.Item>
                    ) : null}

                    <div className="flex items-center gap-4 justify-end mt-4 pt-4 border-t border-slate-100">
                      <Button
                        htmlType="submit"
                        appearance="primary"
                        size="large"
                        loading={submitting}
                        disabled={initiateFieldsLocked}
                      >
                        {submitting ? "Submitting…" : "Initiate Termination"}
                      </Button>
                    </div>
                  </Form>
                </Card>
              </>
            )}
          </div>
        ) : null}

        <div className="space-y-3">
          {!canOpenDetail ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 m-0">
              You do not have termination list access. Ask an admin to assign
              TERMINATION_VIEW (or initiate / approvals permissions).
            </p>
          ) : null}
          {canOpenDetail ? (
            <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden w-full min-w-0">
              <ConfigurableTable<TerminationRequestDto>
                columns={columns}
                data={sortedRows}
                loading={isLoading || isFetching}
                rowKey="id"
                emptyMessage="No termination requests."
                bordered={true}
                hoverable
                className="[&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_th:first-child]:sm:!pl-6 [&_td:first-child]:!pl-4 [&_td:first-child]:sm:!pl-6 [&_th:last-child]:!pr-4 [&_th:last-child]:sm:!pr-6 [&_td:last-child]:!pr-4 [&_td:last-child]:sm:!pr-6"
                spacing={0}
                configOptions={{
                  persistenceKey: "separation-termination-requests",
                }}
                renderColumnSelector={(selector) => (
                  <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
                    <h3 className="text-base font-semibold text-slate-900 m-0">
                      Termination Requests ({sortedRows.length})
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      {selector}
                    </div>
                  </div>
                )}
              />
            </div>
          ) : null}
        </div>

        <TerminationViewModal record={viewRow} onClose={closeView} />

        <Modal
          centered
          transitionName=""
          maskTransitionName=""
          title={<span className="text-xl font-semibold text-slate-900">Termination Decision</span>}
          open={!!actionRow}
          onCancel={() => {
            setActionRow(null);
            actionForm.resetFields();
          }}
          onOk={() => void submitAction()}
          confirmLoading={acting}
          destroyOnClose
          width={480}
        >
          <div className="border-t border-slate-200 -mx-6 mb-6" />
          {actionRow ? (
            <div className="mb-4 text-sm text-slate-600 space-y-1">
              <div>
                <span className="font-medium text-slate-700">Employee: </span>
                {terminationEmployeeLabel(actionRow)}
              </div>
            </div>
          ) : null}

          <Form
            form={actionForm}
            layout="vertical"
            initialValues={{ decision: "approved" }}
            requiredMark={separationFormRequiredMark}
          >
            <Form.Item
              name="decision"
              label="Decision"
              rules={[{ required: true, message: "Select approve or reject" }]}
            >
              <Radio.Group>
                <Radio value="approved">Approve</Radio>
                <Radio value="rejected">Reject</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="remarks"
              label="Remarks"
              rules={[
                {
                  required: true,
                  message: "Remarks are required",
                },
              ]}
            >
              <Input.TextArea
                className={separationTextAreaClassName}
                rows={3}
                maxLength={2000}
                showCount
                placeholder="Remarks"
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          centered
          transitionName=""
          maskTransitionName=""
          title="Cancel termination"
          open={!!cancelRow}
          onCancel={() => {
            setCancelRow(null);
            cancelForm.resetFields();
          }}
          onOk={() => void submitCancel()}
          okText="Submit"
          confirmLoading={cancelling}
          destroyOnClose
          width={480}
        >
          <Form
            form={cancelForm}
            layout="vertical"
            requiredMark={separationFormRequiredMark}
          >
            <Form.Item
              name="reason"
              label="Reason"
              rules={
                cancelRow?.workflow?.isCompleted
                  ? [
                    {
                      required: true,
                      message: "Enter a reason",
                    },
                  ]
                  : undefined
              }
            >
              <Input.TextArea
                className={separationTextAreaClassName}
                rows={3}
                placeholder="Reason for cancellation"
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default TerminationListPage;
